import { Show, createEffect, createMemo, onCleanup, onMount, type JSX } from 'solid-js';
import { Portal } from 'solid-js/web';
import dialogStyles from './Dialog.module.css';
import styles from './Modal.module.css';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: JSX.Element;
  subtitle?: JSX.Element;
  leading?: JSX.Element;
  maxWidth?: string;
  busy?: boolean;
  children: JSX.Element;
}

const FOCUSABLE_SELECTOR =
  'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function getFocusable(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))
    .filter(el => el.offsetParent !== null || el === document.activeElement);
}

export default function Modal(props: ModalProps) {
  let mouseDownInsideDialog = false;
  let dialogRef: HTMLDivElement | undefined;
  let previousActive: HTMLElement | null = null;

  function handleKeyDown(e: KeyboardEvent) {
    if (!props.open) return;
    if (e.key === 'Escape') {
      // A dropdown open inside the dialog claims Escape for itself and marks it handled.
      if (e.defaultPrevented) return;
      props.onClose();
      return;
    }
    if (e.key === 'Tab' && dialogRef) {
      const focusables = getFocusable(dialogRef);
      if (focusables.length === 0) {
        e.preventDefault();
        dialogRef.focus();
        return;
      }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey && (active === first || !dialogRef.contains(active))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (active === last || !dialogRef.contains(active))) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  function handleOverlayMouseDown(e: MouseEvent) {
    if (e.target === e.currentTarget) mouseDownInsideDialog = false;
  }
  function handleDialogMouseDown() {
    mouseDownInsideDialog = true;
  }
  function handleOverlayMouseUp(e: MouseEvent) {
    if (e.target === e.currentTarget && !mouseDownInsideDialog) props.onClose();
    mouseDownInsideDialog = false;
  }

  onMount(() => document.addEventListener('keydown', handleKeyDown));
  onCleanup(() => document.removeEventListener('keydown', handleKeyDown));

  const isOpen = createMemo(() => props.open);

  createEffect(() => {
    if (isOpen() && dialogRef) {
      previousActive = document.activeElement as HTMLElement | null;
      const focusables = getFocusable(dialogRef);
      (focusables[0] ?? dialogRef).focus();
    } else if (!isOpen() && previousActive) {
      previousActive.focus();
      previousActive = null;
    }
  });

  return (
    <Show when={props.open}>
      {/* Portalled because .dialog carries will-change: transform, which makes it the containing
          block for a nested modal's position: fixed overlay and clips it to the outer panel. */}
      <Portal mount={document.body}>
        <div
          class={dialogStyles.overlay}
          onMouseDown={handleOverlayMouseDown}
          onMouseUp={handleOverlayMouseUp}
        >
          <div
            ref={dialogRef}
            class={`${dialogStyles.dialog} ${styles.modalBody}`}
            role="dialog"
            aria-modal="true"
            tabIndex={-1}
            style={{ 'max-width': props.maxWidth ?? '520px' }}
            onMouseDown={handleDialogMouseDown}
          >
            <Show when={props.title || props.leading}>
              <div class={styles.header}>
                <Show when={props.leading}>{props.leading}</Show>
                <div class={styles.headerText}>
                  <Show when={props.title}>
                    <h3 class={styles.title}>{props.title}</h3>
                  </Show>
                  <Show when={props.subtitle}>
                    <div class={styles.subtitle}>{props.subtitle}</div>
                  </Show>
                </div>
                <button class={styles.closeBtn} onClick={props.onClose} aria-label="Close">
                  <i class="fa-solid fa-xmark" />
                </button>
              </div>
            </Show>
            <div class={styles.content}>{props.children}</div>
            <Show when={props.busy}>
              <div class={styles.busyOverlay}>
                <i class="fa-solid fa-spinner fa-spin fa-2x" />
              </div>
            </Show>
          </div>
        </div>
      </Portal>
    </Show>
  );
}
